import { useRef, useState } from 'react';
import type { FolderContentApi, FolderDataState } from '../types';
import { getAvailablePermissions } from './useFileSelection';

interface UseFolderDataArgs {
  folderPath: string;
  groups: Array<any>;
  view: string;
  folderContent: FolderContentApi;
}

export const useFolderData = ({
  folderPath,
  groups,
  view,
  folderContent,
}: UseFolderDataArgs): FolderDataState => {
  const [files, setFiles] = useState<Array<any>>([]);
  const [unfilteredFiles, setUnfilteredFiles] = useState<Array<any>>([]);
  const [totalFiles, setTotalFiles] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [folders, setFolders] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(false);
  const [showContent, toggleContent] = useState(false);
  const [loadingMoreFiles, setLoadingMoreFiles] = useState(false);
  const [folderError, setFolderError] = useState('');
  const [folderSuccess, setFolderSuccess] = useState('');
  const [pathMenu, setPathMenu] = useState<Array<string>>([]);
  const [folderNames, setFolderNames] = useState<Array<any>>([]);
  const [groupLabels, setGroupLabels] = useState<Array<any>>([]);
  const [availablePermissions, setAvailablePermissions] = useState<Array<any>>(
    []
  );
  const [activeDownloadMenu, setActiveDownloadMenu] = useState<string | null>(
    null
  );
  const loadingMoreFilesRef = useRef(false);

  const updatePermissionList = (items: Array<any>) => {
    setAvailablePermissions(getAvailablePermissions(items, groups));
  };

  const getFolderInfo = async (path: string) => {
    try {
      const data = await folderContent.getFolderInfo(path);

      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return;
      }

      const groupItems: Array<any> = [];
      const accessLevelArray = JSON.parse(data.access_level);

      Array.isArray(accessLevelArray) &&
        accessLevelArray.forEach((item: any) => {
          if (groups.some((group) => group.ID === item && item > 1)) {
            groups.forEach((group) => {
              if (group.ID === item) {
                groupItems.push(group);
              }
            });
          }
        });

      setGroupLabels(groupItems);
    } catch (error) {
      console.error('Error fetching folder:', error);
    }
  };

  const getInitialFiles = async () => {
    setLoading(true);
    setFolderError('');
    setPage(0);

    const thumb = view === 'list' ? 'small' : view;

    try {
      const data = await folderContent.getInitialFiles(folderPath, thumb);

      if (!Array.isArray(data)) {
        setFiles([]);
        setUnfilteredFiles([]);
        setLoading(false);
        toggleContent(true);
        return;
      }

      setFiles(data);
      setUnfilteredFiles(data);
      setLoading(false);
      toggleContent(true);
      updatePermissionList(data);
    } catch (error) {
      console.error('Error fetching initial files:', error);
      setLoading(false);
      toggleContent(true);
    }
  };

  const getFiles = async (pageToLoad: number) => {
    loadingMoreFilesRef.current = true;
    setLoading(true);
    setLoadingMoreFiles(true);
    setFolderError('');

    const thumb = view === 'list' ? 'small' : view;

    try {
      const data = await folderContent.getFiles(folderPath, thumb, pageToLoad);

      if (!Array.isArray(data)) {
        setLoading(false);
        setLoadingMoreFiles(false);
        loadingMoreFilesRef.current = false;
        toggleContent(true);
        return;
      }

      setFiles((currentFiles) => [...currentFiles, ...data]);
      setUnfilteredFiles((currentFiles) => [...currentFiles, ...data]);
      setLoading(false);
      setLoadingMoreFiles(false);
      loadingMoreFilesRef.current = false;
      toggleContent(true);
    } catch (error) {
      console.error('Error fetching files:', error);
      setLoading(false);
      setLoadingMoreFiles(false);
      loadingMoreFilesRef.current = false;
      toggleContent(true);
    }
  };

  const getTotalFiles = async () => {
    try {
      const data = await folderContent.getTotalFiles(folderPath);

      if (
        data &&
        typeof data === 'object' &&
        'total' in data &&
        typeof data.total === 'number'
      ) {
        setTotalFiles(data.total);
      }
    } catch (error) {
      console.error('Error fetching total files:', error);
      setLoading(false);
    }
  };

  const getFolders = async () => {
    setLoading(true);
    setGroupLabels([]);
    setFolderError('');
    const thumb = view === 'list' || view === 'small' ? 'medium' : view;

    try {
      const data = await folderContent.getFolders(folderPath, thumb);

      if (Array.isArray(data)) {
        setFolders(data);
      }
      setLoading(false);

      if (!Array.isArray(data) && data?.error) {
        setFolderError(data.error);
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
      setLoading(false);
    }
  };

  const updatePathMenu = (path: string) => {
    let pathArray: string[] = [];
    if (path !== '/') {
      const newArray = path.split('/');
      newArray.shift();
      pathArray = newArray;
    }
    setPathMenu(pathArray);
  };

  const getPathNames = async (path: string) => {
    if (path !== '/') {
      try {
        const data = await folderContent.getPathNames(path);

        Array.isArray(data) && setFolderNames(data);
      } catch (error) {
        console.error('Error fetching folders:', error);
      }
    } else {
      setFolderNames([]);
    }
  };

  const refresh = () => {
    getFolders().then(() => {
      getInitialFiles();
      getTotalFiles();
    });
    setFolderSuccess('');
  };

  const fetchData = async () => {
    try {
      toggleContent(false);
      await getFolders();
      await getInitialFiles();
      await getTotalFiles();
      updatePathMenu(folderPath);
      getPathNames(folderPath);
      setFolderSuccess('');
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  return {
    files,
    setFiles,
    unfilteredFiles,
    setUnfilteredFiles,
    totalFiles,
    setTotalFiles,
    page,
    setPage,
    folders,
    loading,
    setLoading,
    showContent,
    loadingMoreFiles,
    folderError,
    setFolderError,
    folderSuccess,
    setFolderSuccess,
    pathMenu,
    folderNames,
    groupLabels,
    availablePermissions,
    activeDownloadMenu,
    setActiveDownloadMenu,
    loadingMoreFilesRef,
    refresh,
    fetchData,
    getInitialFiles,
    getFiles,
    getTotalFiles,
    getFolders,
    getFolderInfo,
    getPathNames,
    updatePathMenu,
    updatePermissionList,
  };
};
